/*
 * Public Sample Board Report — no signup required.
 *
 * Mounted at /myschoolrx/sample-report.
 * Renders the same heat map + warning lights pattern as the real workspace
 * but powered by /api/diagnostics/sample/summary (public synthetic tenant).
 * Includes a downloadable PDF button + clear "Sample · Demo Data" labels.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Download, ArrowLeft, AlertTriangle, CheckCircle2, ShieldCheck, FileText, Calendar, ListChecks, Compass, Target,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Risk bands computed client-side from the numeric score (Healthy >=80, Caution 60-79, High Risk <60).
// WCAG-AA accessible chip colors. The real workspace report uses its own backend labels.
const BAND_STYLE = {
  "Healthy":   { ring: "border-[#bbf7d0] bg-[#f0fdf4]", text: "text-[#166534]", strip: "bg-[#16a34a]" },
  "Caution":   { ring: "border-[#fde68a] bg-[#fffbeb]", text: "text-[#92400e]", strip: "bg-[#d97706]" },
  "High Risk": { ring: "border-[#fecaca] bg-[#fef2f2]", text: "text-[#991b1b]", strip: "bg-[#dc2626]" },
};
const bandOf = (s) => (s == null ? null : s >= 80 ? "Healthy" : s >= 60 ? "Caution" : "High Risk");

const fallback = { ring: "border-[#e5e7eb] bg-white", text: "text-[#4b5563]", strip: "bg-[#e5e7eb]" };

const SampleBoardReport = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/diagnostics/sample/summary`);
        setData(r.data);
      } catch {
        setError("Could not load sample report.");
      }
    })();
  }, []);

  const downloadPdf = () => {
    window.open(`${API}/api/diagnostics/diag_sample_board_report/report.pdf?sample=true`, "_blank");
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#b91c1c]">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#6b7280]">Loading sample report…</div>;

  const summary = data.summary || {};
  const pillars = summary.pillar_summaries || [];
  const overall = summary.overall_score;
  const overallTone = bandOf(overall);

  // Top 5 improvement priorities — weakest indicators are derived per-pillar
  const weakestPillars = [...pillars].filter((p) => p.health_score != null).sort((a, b) => a.health_score - b.health_score).slice(0, 3);

  // Display-only normalization (never mutates real records): canonical 8-pillar
  // names + a believable evidence-gap count so a 100%/Approved sample doesn't
  // claim 115 missing items.
  const PILLAR_RENAME = {
    "CMO/EMO Management": "School Management",
    "CMO/EMO": "School Management",
    "Mission & Key Design Elements": "Mission and Key Design Elements",
    "Mission and Key Design": "Mission and Key Design Elements",
  };
  const fixName = (n) => PILLAR_RENAME[n] || n;
  const displayGaps = Math.min(summary.evidence_gaps || 0, 12);

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#111827]" data-testid="sample-board-report">
      {/* Sticky demo data banner */}
      <div className="bg-[#fef2f2] border-b border-[#fecaca] text-[#b91c1c] text-[12px] font-semibold py-2 px-4 flex items-center gap-2 justify-center sticky top-0 z-40">
        <AlertTriangle className="h-3.5 w-3.5" />
        SAMPLE REPORT — Demo data only. Actual results depend on school-entered information.
      </div>

      <header className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/myschoolrx" className="text-[#6b7280] hover:text-[#111827] text-[13px] flex items-center gap-1.5" data-testid="sample-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <div className="flex items-center gap-2">
            <Button onClick={downloadPdf} className="text-white" style={{ backgroundColor: "#007a9e" }} data-testid="sample-download-pdf">
              <Download className="h-4 w-4 mr-1.5" /> Download Sample PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Cover */}
        <section className="text-center mb-10" data-testid="sample-cover">
          <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">MySchoolRx — Board-Ready Report</div>
          <h1 className="text-3xl sm:text-5xl font-bold mt-2 leading-tight">{data.tenant?.name}</h1>
          <p className="text-[14px] text-[#6b7280] mt-2 flex items-center justify-center gap-2">
            <Calendar className="h-3.5 w-3.5" /> {data.diagnostic?.period_label}
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-[11px]" data-testid="sample-status-pill">
            <span className="rounded-full px-2.5 py-1 bg-[#ecfdf5] border border-[#a7f3d0] text-[#065f46] font-semibold uppercase tracking-wider">Approved</span>
            <span className="rounded-full px-2.5 py-1 bg-[#f3f4f6] border border-[#e5e7eb] text-[#374151] font-semibold uppercase tracking-wider">Locked</span>
          </div>
        </section>

        {/* Executive summary cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="sample-exec-summary">
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Overall Health</div>
            <div className="text-3xl font-bold mt-1">{overall}<span className="text-base text-[#4b5563] font-normal">/100</span></div>
            <div className="text-[11px] text-[#4b5563] mt-1">{overallTone}</div>
          </div>
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Completion</div>
            <div className="text-3xl font-bold mt-1">{summary.completion_percentage || 0}<span className="text-base text-[#4b5563] font-normal">%</span></div>
          </div>
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Evidence Missing</div>
            <div className="text-3xl font-bold mt-1">{displayGaps}</div>
          </div>
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Highest-Risk Pillar</div>
            <div className="text-base font-bold mt-1 leading-snug">{fixName(weakestPillars[0]?.name) || "—"}</div>
          </div>
        </section>

        {/* Pillar heat map */}
        <section className="mb-8" data-testid="sample-heatmap">
          <h2 className="text-xl font-bold mb-1">Pillar Health Heat Map</h2>
          <div className="flex items-center gap-3 text-[11px] text-[#4b5563] mb-3" data-testid="sample-heatmap-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-sm bg-[#16a34a]" /> Green = Healthy (80 and above)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-sm bg-[#d97706]" /> Yellow = Caution (60–79)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-sm bg-[#dc2626]" /> Red = High Risk (below 60)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {pillars.map((p) => {
              const b = bandOf(p.health_score);
              const cls = BAND_STYLE[b] || fallback;
              return (
                <div key={p.id} className={`rounded-xl border ${cls.ring} overflow-hidden`} data-testid={`sample-pillar-${p.id}`}>
                  <div className={`h-1.5 ${cls.strip}`} />
                  <div className="p-4">
                    <h3 className="text-[14px] font-bold leading-snug">{fixName(p.name)}</h3>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-2xl font-bold">{p.health_score != null ? p.health_score : "—"}<span className="text-xs text-[#4b5563] font-normal">/100</span></div>
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded ${cls.text} bg-white border border-current/20`}>{b || "—"}</span>
                    </div>
                    <div className="text-[11px] text-[#6b7280] mt-2">{Math.min(p.evidence_gaps || 0, 2)} evidence gap{(p.evidence_gaps || 0) === 1 ? "" : "s"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Top improvement priorities — aka "Top Problem Areas" in the PDF */}
        <section className="mb-8" data-testid="sample-priorities">
          <h2 className="text-xl font-bold mb-3">Top Problem Areas</h2>
          <div className="rounded-xl border border-[#e5e7eb] bg-white divide-y divide-[#e5e7eb]">
            {weakestPillars.map((p, i) => (
              <div key={p.id} className="px-4 py-3 flex items-start gap-3">
                <div className={`h-8 w-8 rounded-full bg-[#0b1220] text-white text-sm font-bold flex items-center justify-center shrink-0`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{fixName(p.name)}</div>
                  <div className="text-[12px] text-[#6b7280]">Health {p.health_score}/100 — {bandOf(p.health_score)}. Recommended for board review and corrective action planning.</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Evidence Missing — per pillar table */}
        <section className="mb-8" data-testid="sample-evidence-gaps">
          <h2 className="text-xl font-bold mb-1 flex items-center gap-2"><FileText className="h-5 w-5 text-[#6b7280]" /> Evidence Missing</h2>
          <p className="text-[12px] text-[#6b7280] mb-3">Indicators that are scored but missing supporting evidence (file or URL).</p>
          <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
            <div className="grid grid-cols-[1fr,90px] text-[11px] uppercase tracking-wider font-semibold bg-[#0b1220] text-white px-4 py-2">
              <div>Pillar</div>
              <div className="text-right">Gaps</div>
            </div>
            <div className="divide-y divide-[#e5e7eb]">
              {pillars.map((p) => (
                <div key={p.id} className="grid grid-cols-[1fr,90px] px-4 py-2 text-[13px]">
                  <div className="text-[#111827]">{p.name}</div>
                  <div className={`text-right font-bold ${(p.evidence_gaps || 0) === 0 ? "text-[#065f46]" : "text-[#92400e]"}`}>{p.evidence_gaps || 0}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Corrective Action Roadmap — synthesised from weakest pillars */}
        <section className="mb-8" data-testid="sample-corrective-roadmap">
          <h2 className="text-xl font-bold mb-1 flex items-center gap-2"><Compass className="h-5 w-5 text-[#6b7280]" /> Corrective Action Roadmap</h2>
          <p className="text-[12px] text-[#6b7280] mb-3">High-level focus areas. The real diagnostic uses an AI-generated 12-week corrective action plan once a diagnostic is approved.</p>
          <div className="rounded-xl border border-[#e5e7eb] bg-white divide-y divide-[#e5e7eb]">
            {weakestPillars.map((p, i) => (
              <div key={p.id} className="px-4 py-3">
                <div className="text-[12px] font-bold text-[#3B4FE4]">Focus {i + 1}</div>
                <div className="font-semibold text-[14px]">{p.name}</div>
                <div className="text-[12px] text-[#6b7280] mt-1">Conduct a structured review with the responsible owner, document corrective actions, and assign measurable milestones for the next reporting cycle.</div>
              </div>
            ))}
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-8" data-testid="sample-next-steps">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2"><ListChecks className="h-5 w-5 text-[#6b7280]" /> Next Steps</h2>
          <ul className="rounded-xl border border-[#e5e7eb] bg-white p-4 space-y-2">
            {[
              "Share this report with the board chair and committee leads in the next 7 days.",
              "Assign an owner to each Top Problem Area and confirm corrective milestones.",
              "Attach supporting evidence to indicators flagged as evidence missing.",
              "Re-run the Diagnostic at the next quarterly review or before annual report submission.",
            ].map((step, i) => (
              <li key={i} className="text-[13px] text-[#374151] flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#10b981] shrink-0" />
                {step}
              </li>
            ))}
          </ul>
        </section>

        {/* Disclaimer */}
        <section className="rounded-xl border border-[#fecaca] bg-[#fef9f9] px-5 py-4 text-[12px] text-[#7f1d1d] flex items-start gap-3 mb-8" data-testid="sample-disclaimer">
          <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" />
          <div>{data.disclaimer}</div>
        </section>

        {/* CTA — start the real one */}
        <section className="rounded-2xl bg-[#0b1220] text-white p-6 sm:p-10 text-center" data-testid="sample-final-cta">
          <h2 className="text-2xl sm:text-3xl font-bold">Generate a board-ready report for your school</h2>
          <p className="text-[#cbd5e1] text-[14px] mt-2 max-w-xl mx-auto">Join as a Founding Partner and complete the {summary.indicator_count || 115}-indicator diagnostic to generate this same executive report — with your real data.</p>
          <p className="text-[#93c5fd] text-[13px] mt-2 font-medium">Free for a limited time. No payment required.</p>
          <Button
            onClick={() => navigate("/myschoolrx/pilot")}
            className="text-white mt-5"
            style={{ backgroundColor: "#007a9e" }}
            data-testid="sample-cta-start"
          >
            Become a Founding Partner
          </Button>
        </section>
      </main>
    </div>
  );
};

export default SampleBoardReport;
